import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react';
import cn from 'classnames';

import { Defer, useI18nLanguage } from '@mopla/utils';

export type TWidgetId = string | number;

// Define global types for Google reCAPTCHA
declare global {
	interface Window {
		grecaptcha?: {
			render: (element: HTMLElement, options: RecaptchaOptions) => TWidgetId;
			execute: (widgetId: TWidgetId) => Promise<string>;
			reset: (widgetId: TWidgetId) => void;
		};
		onRecaptchaLoaded?: () => void;
	}
}

// Interface for reCAPTCHA options passed to the render method
interface RecaptchaOptions {
	sitekey: string;
	callback: (token: string) => void;
	'error-callback'?: () => void;
	'expired-callback'?: () => void;
	size: 'invisible' | 'normal';
}

// Props for the Recaptcha component
export interface RecaptchaProps {
	siteKey: string;
	onVerify: (token: string) => Promise<void> | void;
	onError?: () => void;
	onExpired?: () => void;
	invisible?: boolean;
	className?: string;
}

// Interface for the methods exposed via ref
export interface RecaptchaHandle {
	executeRecaptcha: () => Promise<string>;
	resetRecaptcha: () => void;
}

// The component using forwardRef to expose methods to parent components
export const Recaptcha = forwardRef<RecaptchaHandle, RecaptchaProps>(
	(
		{ siteKey, onVerify, onError, onExpired, invisible = true, className },
		ref
	) => {
		const recaptchaRef = useRef<HTMLDivElement>(null);
		const widgetIdRef = useRef<TWidgetId | null>(null);
		const solvedDeferRef = useRef<Defer<string> | null>(null);
		const lng = useI18nLanguage();

		// Execute the reCAPTCHA verification
		const executeRecaptcha = useCallback(async (): Promise<string> => {
			if (widgetIdRef.current !== null && window.grecaptcha) {
				// Reset previous defer if exists
				if (solvedDeferRef.current) {
					solvedDeferRef.current = null;
				}

				// Reset the widget before executing to clear any previous state
				window.grecaptcha.reset(widgetIdRef.current);

				// Create Defer BEFORE executing to avoid race condition
				const d = new Defer<string>();
				solvedDeferRef.current = d;

				await window.grecaptcha.execute(widgetIdRef.current);

				return d.promise;
			}
			return Promise.reject('reCAPTCHA not ready');
		}, []);

		// Reset the reCAPTCHA widget
		const resetRecaptcha = useCallback(() => {
			if (widgetIdRef.current !== null && window.grecaptcha) {
				window.grecaptcha.reset(widgetIdRef.current);
			}
		}, []);

		const onVerifyHandler = useCallback(
			async (token: string) => {
				await onVerify(token);

				solvedDeferRef.current?.resolve(token);
			},
			[onVerify]
		);

		const onErrorHandler = useCallback(() => {
			if (onError) {
				onError();
			}

			solvedDeferRef.current?.reject(null);
		}, [onError]);

		const onExpiredHandler = useCallback(() => {
			if (onExpired) {
				onExpired();
			}

			solvedDeferRef.current?.reject(null);
		}, [onExpired]);

		// Expose methods to parent component via ref
		useImperativeHandle(
			ref,
			() => ({
				executeRecaptcha,
				resetRecaptcha,
			}),
			[executeRecaptcha, resetRecaptcha]
		);

		// Initialize the reCAPTCHA widget
		const initRecaptcha = useCallback(() => {
			// Only render if widget hasn't been initialized yet
			if (
				recaptchaRef.current &&
				window.grecaptcha &&
				widgetIdRef.current === null
			) {
				try {
					widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
						sitekey: siteKey,
						callback: onVerifyHandler,
						'error-callback': onErrorHandler,
						'expired-callback': onExpiredHandler,
						size: invisible ? 'invisible' : 'normal',
					});
				} catch (error) {
					// If we get an error about already being rendered, log it but don't crash
					console.warn('reCAPTCHA initialization error:', error);
					// Try to find the existing widget ID if possible
					if (recaptchaRef.current.getAttribute('data-widget-id')) {
						widgetIdRef.current = Number(
							recaptchaRef.current.getAttribute('data-widget-id')
						);
					}
				}
			}
		}, [siteKey, onVerifyHandler, onErrorHandler, onExpiredHandler, invisible]);

		useEffect(() => {
			// Check if grecaptcha is already loaded
			if (
				typeof window !== 'undefined' &&
				window.grecaptcha &&
				typeof window.grecaptcha.render === 'function'
			) {
				initRecaptcha();
			} else {
				// If not loaded yet, set up the callback
				window.onRecaptchaLoaded = initRecaptcha;

				// Check if the script is already added
				const existingScript = document.querySelector(
					'script[src^="https://www.google.com/recaptcha/api.js"]'
				);

				if (!existingScript) {
					// Add the script if not present
					const script = document.createElement('script');
					script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoaded&render=explicit&hl=${lng}`;
					script.async = true;
					script.defer = true;
					script.dataset.test = 'dsf234';
					document.body.appendChild(script);
				}
			}

			return () => {
				delete window.onRecaptchaLoaded;
			};
		}, [initRecaptcha, lng]);

		return (
			<div
				ref={recaptchaRef}
				className={cn('g-recaptcha', className)}
				data-size="invisible"
			/>
		);
	}
);

// Export helper functions to be used from other components
export const getRecaptchaInstance = () => {
	if (typeof window !== 'undefined' && window.grecaptcha) {
		return window.grecaptcha;
	}
	return null;
};

// Display name for better debugging
Recaptcha.displayName = 'Recaptcha';
